import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { GStatic } from './GStatic';
import chroma from 'chroma-js';
import _ from 'lodash';
import { environment } from '../../environments/environment';

export interface PersonalizationModel {
  type?: 'full' | 'standard';
  colorPrimary?: string;
  colorSecondary?: string;
  logoFull?: string;
  logoFullRtl?: string;
  logoIcon?: string;
  logoFull2x?: string;
  logoFullRtl2x?: string;
  logoIcon2x?: string;

  companyName?: string;
  companyNameAltLang?: string;
  companyNameShort?: string;

  prefixTitle?: string;
  favIcon?: string;

  darkMode?: boolean;
  copyright?: string;

  fontName?: string;
  fontFamilyUrls?: string[];
  backgroundImage?: string;
}

export interface PersonaApi {
  client?: string;
  client_id?: string;
  accent_color?: string;
  dark_mode?: boolean;
  logo?: string;
  logo_ar?: string;
  logo_icon?: string;
  logo_printing?: string;
  primary_color?: string;
  secondary_color?: string;
  secondary_name?: string;
  pdfPattern?: boolean;
  background_image?: string;
  font?: {
    normal: string;
    bold: string;
    italics: string;
    bolditalics: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class PersonalizationService {
  // #region
  type: 'full' | 'standard';

  colorPrimary1: string = '';
  colorSecondary: string = '';
  colorAccent1: string = '';

  colorAccent = {
    a50: '#f8fafc',
    a100: '#f1f5f9', ///
    a200: '#e2e8f0', ///////
    a300: '#cbd5e1', ////
    a400: '#94a3b8', ////////
    a90001: '#71747d', /////
    a500: '#64748b', ////////
    a600: '#475569', ///
    a700: '#334155',
    a800: '#1e293b', // 7 main
    a900: '#0f172a', /////

    rgb50: '248,250,252',
    rgb100: '241,245,249',
    rgb200: '226,232,240',
    rgb300: '203,213,225',
    rgb400: '148,163,184',
    rgb500: '100,116,139',
    rgb600: '71,85,105',
    rgb700: '51,65,85',
    rgb800: '30,41,59',
    rgb900: '15,23,42',
  };

  colorPrimary = {
    a50: '#fefdf1',
    a100: '#fefae4',
    a200: '#fdf6c9',
    a300: '#fbf1a8',
    a400: '#faec80',
    a500: '#f9e744', // main
    a600: '#e2d13e', //
    a700: '#c8b937',
    a800: '#aa9e2f',
    a900: '#867c25',

    rgb50: '254,253,241',
    rgb100: '254,250,228',
    rgb200: '253,246,201',
    rgb300: '251,241,168',
    rgb400: '250,236,128',
    rgb500: '249,231,68',
    rgb600: '226,209,62',
    rgb700: '200,185,55',
    rgb800: '170,158,47',
    rgb900: '134,124,37',
  };

  logoFull: string = ''; // w-24
  logoFullRtl: string = ''; // w-24
  logoPrint: string;

  logoIcon: string = ''; // w-8
  logoFull2x: string;
  logoFullRtl2x: string;

  logoIcon2x: string;
  companyName: string;
  companyNameAltLang: string;

  companyNameShort: string;
  prefixTitle: string;

  favIcon: string;
  darkMode: boolean;

  copyright: string;
  fontName: string;

  pdfPattern: boolean = true;
  fonts: {
    normal: string;
    bold: string;
    italics: string;
    bolditalics: string;
  };
  secondaryName: string;
  usePersonaState: boolean;
  backgroundImage: string;

  constructor(@Inject(DOCUMENT) private document: Document) {}

  /**
   * Initialize the persona
   * @param persona - Persona data from API
   * @param secondaryName - Secondary name for the client
   * @param clientId - Client ID from user data
   */
  init(persona?: PersonaApi, secondaryName?: string, clientId?: string): void {
    if (persona) {
      this.setTheme(persona, secondaryName, clientId);
    } else {
      this.resetThemeToDefault(secondaryName);
    }
  }

  set usePersona(state: boolean) {
    this.usePersonaState = state;
  }

  /**
   * Set background image programmatically
   * @param imagePath - Path to the background image (e.g., '/assets/images/bg_upscaled.jpg')
   */
  setBackgroundImage(imagePath: string): void {
    this.backgroundImage = imagePath;
    this.prepareCssVars();
  }

  /**
   * Remove background image
   */
  removeBackgroundImage(): void {
    this.backgroundImage = '';
    this.prepareCssVars();
  }

  /**
   * Set theme colors and other properties
   * @param persona - Persona data from API
   * @param secondaryName - Secondary name for the client
   * @param clientId - Client ID from user data
   */
  setTheme(persona?: PersonaApi, secondaryName?: string, clientId?: string): void {
    // If persona is not provided, set default values
    if (!persona) {
      persona = {
        primary_color: '#1e293b',
        accent_color: '#f9e744',
        secondary_color: '',
        secondary_name: secondaryName || null, // Use provided secondaryName if available
        logo: '/assets/images/logo/logo-text-on-dark.svg',
        logo_icon: '/assets/images/logo/logo-on-dark.svg',
        logo_ar: '/assets/images/logo/logo-text-on-dark.svg',
        logo_printing: '/assets/images/logo/logo 1@3x.png',
        dark_mode: true,
        pdfPattern: true,
      };
    }

    // Special handling for SWCC client
    if (persona.client === 'SWCC') {
      persona.pdfPattern = false; // Disable PDF pattern for SWCC
      persona.font = {
        normal: `${environment.base_url}/assets/fonts/swcc/SWCC-Regular.ttf`,
        bold: `${environment.base_url}/assets/fonts/swcc/SWCC-Medium.ttf`,
        italics: `${environment.base_url}/assets/fonts/swcc/SWCC-Bold.ttf`,
        bolditalics: `${environment.base_url}/assets/fonts/swcc/SWCC-Bold.ttf`,
      };
    }

    // Background image configuration - Currently enabled for all users for testing
    // TODO: Add client_id check when ready to enable for specific clients only
    if (
      clientId === 'fd390e84-dcf8-4fbb-b28b-b04e76a176fb' ||
      clientId === '4e771255-959e-42d6-a137-e39a07f90f2e'
    ) {
      // if (!persona.background_image) {
      persona.background_image = '/assets/images/bg_upscaled.jpg';
    }

    // Assign values from persona
    this.colorPrimary1 = persona.accent_color || '#1e293b'; // Fallback to default if undefined
    this.colorAccent1 = persona.primary_color || '#f9e744'; // Fallback to default if undefined
    this.logoFull = persona.logo || '/assets/images/logo/logo-text-on-dark.svg';
    this.logoIcon = persona.logo_icon || '/assets/images/logo/logo-on-dark.svg';
    this.logoFullRtl = persona.logo_ar || '/assets/images/logo/logo-text-on-dark.svg';
    this.secondaryName = secondaryName; // Fallback to provided secondaryName
    this.colorSecondary = persona.secondary_color || '';
    this.darkMode = persona.dark_mode ?? true; // Default to true if undefined
    this.logoPrint = persona.logo_printing || this.logoFull;
    this.pdfPattern = persona.pdfPattern ?? true; // Default to true if undefined
    this.fonts = persona.font || null;
    this.backgroundImage = persona.background_image || '';

    this.prepareCssVars();
  }

  /**
   * Reset theme to default
   */
  resetThemeToDefault(secondaryName?: string): void {
    this.setTheme(
      {
        primary_color: '#1e293b',
        accent_color: '#f9e744',
        secondary_color: '',
        logo: '/assets/images/logo/logo-text-on-dark.svg',
        logo_icon: '/assets/images/logo/logo-on-dark.svg',
        logo_ar: '/assets/images/logo/logo-text-on-dark.svg',
        logo_printing: '/assets/images/logo/logo 1@3x.png',
        dark_mode: true,
        pdfPattern: true,
      },
      secondaryName,
    );
  }

  prepareCssVars(): void {
    const oldStyle = document.getElementById('personaStyles');
    if (oldStyle) {
      oldStyle.remove();
    }
    if (this.usePersonaState) {
      const primary = this.prepareColors(this.colorPrimary1, 600, 'primary');
      const accent = this.prepareColors(this.colorAccent1, 800, 'accent');

      const body_dark = {
        '--fuse-bg-app-bar': accent['--fuse-accent-900'],
        '--fuse-bg-app-bar-rgb': accent['--fuse-accent-900-rgb'],
        '--fuse-bg-card': accent['--fuse-accent-800'],
        '--fuse-bg-card-rgb': accent['--fuse-accent-800-rgb'],
        '--fuse-bg-default': accent['--fuse-accent-900'],
        '--fuse-bg-default-rgb': accent['--fuse-accent-900-rgb'],
        '--fuse-bg-dialog': accent['--fuse-accent-800'],
        '--fuse-bg-dialog-rgb': accent['--fuse-accent-800'],
        '--fuse-bg-hover': 'rgba(255, 255, 255, 0.05)',
        '--fuse-bg-hover-rgb': '255,255,255',
        '--fuse-bg-status-bar': accent['--fuse-accent-900'],
        '--fuse-bg-status-bar-rgb': accent['--fuse-accent-900-rgb'],
        '--fuse-text-default': '#FFFFFF',
        '--fuse-text-default-rgb': '255,255,255',
        '--fuse-text-secondary': accent['--fuse-accent-400'],
        '--fuse-text-secondary-rgb': accent['--fuse-accent-400-rgb'],
        '--fuse-text-hint': accent['--fuse-accent-500'],
        '--fuse-text-hint-rgb': accent['--fuse-accent-500-rgb'],
        '--fuse-text-disabled': accent['--fuse-accent-600'],
        '--fuse-text-disabled-rgb': accent['--fuse-accent-600-rgb'],
        '--fuse-border': 'rgba(241,245,249,0.12)',
        '--fuse-border-rgb': '241,245,249',
        '--fuse-divider': 'rgba(241,245,249,0.12)',
        '--fuse-divider-rgb': '241,245,249',
        '--fuse-icon': accent['--fuse-accent-400'],
        '--fuse-icon-rgb': accent['--fuse-accent-400-rgb'],
        '--fuse-mat-icon': accent['--fuse-accent-400'],
        '--fuse-mat-icon-rgb': accent['--fuse-accent-400-rgb'],
      };

      const backgroundImageCss = this.backgroundImage
        ? `
        body::before {
          content: '';
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-image: url('${this.backgroundImage}');
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          background-attachment: fixed;
          z-index: -1;
          opacity: 0.9;
        }
        body {
          position: relative;
        }
        body.dark .mat-drawer-container, body .dark .mat-drawer-container{
          background-color: ${accent['--fuse-accent-900']}99 !important;
        }
        /* Lower opacity for backgrounds when bg image is present */
        .bg-card, .dark .bg-card {
          background-color: rgba(var(--fuse-accent-800-rgb), 0.7) !important;
        }
        .bg-accent-800, .dark .bg-accent-800 {
          background-color: rgba(var(--fuse-accent-800-rgb), 0.7) !important;
        }
        /* Lower opacity for bg-accent-900 EXCEPT for drawers/sidebars */
        .bg-accent-900:not(.mat-drawer):not(.mat-sidenav),
        .dark .bg-accent-900:not(.mat-drawer):not(.mat-sidenav) {
          background-color: rgba(var(--fuse-accent-900-rgb), 0.6) !important;
        }
        .bg-accent-700, .dark .bg-accent-700 {
          background-color: rgba(var(--fuse-accent-700-rgb), 0.6) !important;
        }
        .fuse-card {
          background-color: rgba(var(--fuse-accent-800-rgb), 0.65) !important;
        }

        /* Sidebar navigation - 100% visible (no transparency) */
        fuse-vertical-navigation,
        fuse-vertical-navigation .fuse-vertical-navigation-wrapper,
        fuse-vertical-navigation .fuse-vertical-navigation-content,
        fuse-vertical-navigation .fuse-vertical-navigation-header,
        fuse-vertical-navigation .fuse-vertical-navigation-footer,
        fuse-vertical-navigation .fuse-vertical-navigation-content-header,
        fuse-vertical-navigation .fuse-vertical-navigation-content-footer {
          background-color: rgb(var(--fuse-accent-900-rgb)) !important;
          opacity: 1 !important;
        }

        /* Ensure all navigation items maintain full visibility */
        fuse-vertical-navigation fuse-vertical-navigation-basic-item,
        fuse-vertical-navigation fuse-vertical-navigation-collapsable-item,
        fuse-vertical-navigation fuse-vertical-navigation-group-item,
        fuse-vertical-navigation fuse-vertical-navigation-aside-item,
        fuse-vertical-navigation .fuse-vertical-navigation-item-wrapper,
        fuse-vertical-navigation .fuse-vertical-navigation-item {
          opacity: 1 !important;
        }

        /* Fix mat-drawer (sidebar) background opacity - full opacity for best visibility */
        /* High specificity selectors to override bg-accent classes */
        .mat-drawer,
        .mat-sidenav,
        .dark .mat-drawer,
        .dark .mat-sidenav,
        .mat-drawer.bg-accent-900,
        .mat-sidenav.bg-accent-900,
        .dark .mat-drawer.bg-accent-900,
        .dark .mat-sidenav.bg-accent-900 {
          background-color: rgb(var(--fuse-accent-900-rgb)) !important;
          opacity: 1 !important;
        }
        .mat-drawer-container {
          background-color: ${accent['--fuse-accent-900']}99 !important;
        }
        /* Notification banner - full visibility and strong contrast for better readability */
        .bg-primary-400.bg-opacity-20 {
          background-color: rgb(var(--fuse-primary-400-rgb)) !important;
          opacity: 1 !important;
        }

        /* Ensure notification banner text is fully visible with strong contrast */
        .bg-primary-400.bg-opacity-20 span,
        .bg-primary-400.bg-opacity-20 button,
        .bg-primary-400.bg-opacity-20 .mat-icon {
          opacity: 1 !important;
          color: var(--fuse-primary-900) !important;
          font-weight: 600 !important;
        }

        /* Make the Settings button text even more prominent */
        .bg-primary-400.bg-opacity-20 button.underline {
          color: var(--fuse-primary-900) !important;
          font-weight: 700 !important;
          text-shadow: 0 0 1px rgba(0, 0, 0, 0.1);
        }

        /* ===== VISIBILITY IMPROVEMENTS FOR BACKGROUND IMAGE CLIENT ===== */

        /* Make primary button text white for better contrast */
        .mat-flat-button.mat-primary,
        .mat-raised-button.mat-primary,
        .mat-fab.mat-primary,
        .mat-mini-fab.mat-primary {
          color: #ffffff !important;
        }

        .mat-flat-button.mat-primary .mat-button-wrapper,
        .mat-raised-button.mat-primary .mat-button-wrapper,
        .mat-fab.mat-primary .mat-button-wrapper,
        .mat-mini-fab.mat-primary .mat-button-wrapper {
          color: #ffffff !important;
        }

        /* Improve disabled primary button visibility - much higher opacity */
        .mat-flat-button.mat-primary[disabled],
        .mat-raised-button.mat-primary[disabled],
        .mat-fab.mat-primary[disabled],
        .mat-mini-fab.mat-primary[disabled],
        .mat-flat-button.mat-primary:disabled,
        .mat-raised-button.mat-primary:disabled,
        .mat-fab.mat-primary:disabled,
        .mat-mini-fab.mat-primary:disabled {
          opacity: 0.85 !important;
          background-color: rgba(var(--fuse-primary-500-rgb), 0.85) !important;
          color: rgba(255, 255, 255, 0.95) !important;
        }

        /* Better visibility for disabled buttons in app-header */
        app-header .mat-flat-button.mat-primary[disabled],
        app-header .mat-raised-button.mat-primary[disabled],
        app-header .mat-flat-button.mat-primary:disabled,
        app-header .mat-raised-button.mat-primary:disabled {
          opacity: 0.9 !important;
          background-color: rgba(var(--fuse-primary-500-rgb), 0.85) !important;
          color: rgba(255, 255, 255, 0.95) !important;
        }

        app-header .mat-flat-button.mat-primary[disabled] .mat-button-wrapper,
        app-header .mat-raised-button.mat-primary[disabled] .mat-button-wrapper,
        app-header .mat-flat-button.mat-primary:disabled .mat-button-wrapper,
        app-header .mat-raised-button.mat-primary:disabled .mat-button-wrapper {
          color: rgba(255, 255, 255, 0.95) !important;
        }

        /* Improve disabled stroked/outlined button visibility */
        .mat-stroked-button[disabled],
        .mat-stroked-button:disabled,
        button[mat-stroked-button][disabled],
        button[mat-stroked-button]:disabled {
          opacity: 0.85 !important;
          border-color: rgba(255, 255, 255, 0.7) !important;
          color: rgba(255, 255, 255, 0.85) !important;
        }

        /* Improve disabled icon button visibility */
        .mat-icon-button[disabled],
        .mat-icon-button:disabled,
        button[mat-icon-button][disabled],
        button[mat-icon-button]:disabled {
          opacity: 0.85 !important;
        }

        .mat-icon-button[disabled] .mat-icon,
        .mat-icon-button:disabled .mat-icon {
          color: rgba(255, 255, 255, 0.85) !important;
        }

        /* Improve disabled regular button visibility */
        .mat-button[disabled],
        .mat-button:disabled {
          opacity: 0.85 !important;
          color: rgba(255, 255, 255, 0.85) !important;
        }

        /* Improve disabled raised button (non-primary) visibility */
        .mat-raised-button[disabled]:not(.mat-primary),
        .mat-raised-button:disabled:not(.mat-primary),
        button[mat-raised-button][disabled]:not(.mat-primary),
        button[mat-raised-button]:disabled:not(.mat-primary) {
          opacity: 0.85 !important;
          background-color: rgba(255, 255, 255, 0.25) !important;
          color: rgba(255, 255, 255, 0.85) !important;
        }

        /* Improve disabled menu item visibility */
        .mat-menu-item[disabled],
        .mat-menu-item:disabled,
        button[mat-menu-item][disabled],
        button[mat-menu-item]:disabled {
          opacity: 0.75 !important;
          color: rgba(255, 255, 255, 0.7) !important;
        }

        .mat-menu-item[disabled] .mat-icon,
        .mat-menu-item:disabled .mat-icon {
          color: rgba(255, 255, 255, 0.7) !important;
        }

        /* Improve disabled FAB (floating action button) visibility */
        .mat-fab[disabled]:not(.mat-primary),
        .mat-fab:disabled:not(.mat-primary),
        .mat-mini-fab[disabled]:not(.mat-primary),
        .mat-mini-fab:disabled:not(.mat-primary) {
          opacity: 0.85 !important;
          background-color: rgba(255, 255, 255, 0.25) !important;
          color: rgba(255, 255, 255, 0.85) !important;
        }

        /* Generic catch-all for any other disabled buttons */
        button[disabled]:not([mat-icon-button]):not([mat-menu-item]),
        button:disabled:not([mat-icon-button]):not([mat-menu-item]) {
          opacity: 0.85 !important;
        }

        button[disabled] .mat-icon:not(.mat-primary),
        button:disabled .mat-icon:not(.mat-primary) {
          color: rgba(255, 255, 255, 0.8) !important;
        }

        /* Make gray text white for better visibility */
        .text-gray-600,
        .text-gray-500,
        .text-gray-400,
        .text-secondary {
          color: rgba(255, 255, 255, 0.9) !important;
        }

        /* Improve text-hint and disabled text visibility */
        .text-hint,
        .text-disabled {
          color: rgba(255, 255, 255, 0.7) !important;
        }

        /* Make sure Material typography variants are also white */
        .mat-body,
        .mat-body-1,
        .mat-body-2,
        .mat-caption,
        .mat-small {
          color: rgba(255, 255, 255, 0.9) !important;
        }

        /* Fix disabled toggle button visibility - keep original look, just more visible */
        .mat-slide-toggle.mat-disabled,
        .mat-slide-toggle.mat-slide-toggle-disabled,
        mat-slide-toggle.mat-disabled,
        mat-slide-toggle[disabled] {
          opacity: 1 !important;
        }

        /* Ensure inner elements maintain proper opacity relative to parent */
        .mat-slide-toggle.mat-disabled .mat-slide-toggle-thumb,
        .mat-slide-toggle.mat-disabled .mat-slide-toggle-bar,
        mat-slide-toggle.mat-disabled .mat-slide-toggle-thumb,
        mat-slide-toggle.mat-disabled .mat-slide-toggle-bar,
        mat-slide-toggle[disabled] .mat-slide-toggle-thumb,
        mat-slide-toggle[disabled] .mat-slide-toggle-bar {
          opacity: 1 !important;
        }

        /* Fix button container bleeding - make containers fit buttons exactly */
        .border-2.rounded-full.border-transparent.bg-accent-800 {
          display: inline-flex !important;
          align-items: center !important;
          padding: 0 !important;
          line-height: 1 !important;
          vertical-align: middle !important;
        }

        /* Ensure button takes full container height without extra space */
        .border-2.rounded-full.border-transparent.bg-accent-800 button {
          margin: 0 !important;
          display: flex !important;
          align-items: center !important;
        }

        /* ===== SIDEBAR NAVIGATION DISABLED ITEMS - CLEAR VISUAL DISTINCTION ===== */

        /* Disabled navigation items in sidebar - visually distinct but still visible */
        fuse-vertical-navigation .fuse-vertical-navigation-item.fuse-vertical-navigation-item-disabled {
          cursor: not-allowed !important;
          opacity: 0.45 !important;
          position: relative;
        }

        /* Grayed out icon for disabled nav items */
        fuse-vertical-navigation .fuse-vertical-navigation-item.fuse-vertical-navigation-item-disabled .fuse-vertical-navigation-item-icon {
          color: rgba(var(--fuse-accent-500-rgb), 0.6) !important;
          filter: grayscale(80%) !important;
        }

        /* Grayed out title for disabled nav items */
        fuse-vertical-navigation .fuse-vertical-navigation-item.fuse-vertical-navigation-item-disabled .fuse-vertical-navigation-item-title {
          color: rgba(var(--fuse-accent-400-rgb), 0.7) !important;
        }

        /* Grayed out subtitle for disabled nav items */
        fuse-vertical-navigation .fuse-vertical-navigation-item.fuse-vertical-navigation-item-disabled .fuse-vertical-navigation-item-subtitle {
          color: rgba(var(--fuse-accent-500-rgb), 0.5) !important;
        }

        /* No hover effect for disabled items */
        fuse-vertical-navigation .fuse-vertical-navigation-item.fuse-vertical-navigation-item-disabled:hover {
          background-color: transparent !important;
        }

        /* Badge styling for disabled items */
        fuse-vertical-navigation .fuse-vertical-navigation-item.fuse-vertical-navigation-item-disabled .fuse-vertical-navigation-item-badge {
          opacity: 0.5 !important;
          filter: grayscale(60%) !important;
        }
      `
        : `
        body.dark .mat-drawer-container, body .dark .mat-drawer-container{
          background-color: ${accent['--fuse-accent-900']} !important;
        }
      `;

      let styleTxt = '';

      Object.entries(primary).forEach((item) => {
        styleTxt += item[0] + ': ' + item[1] + ' !important;\n';
      });
      Object.entries(accent).forEach((item) => {
        styleTxt += item[0] + ': ' + item[1] + ' !important;\n';
      });
      /*Object.entries(warn).forEach((item) => {
        styleTxt += item[0] + ': ' + item[1] + ' !important;\n';
      });*/
      Object.entries(body_dark).forEach((item) => {
        styleTxt += item[0] + ': ' + item[1] + ' !important;\n';
      });

      const style = document.createElement('style');
      style.innerHTML = `body {
     ${styleTxt}
    }
    ${backgroundImageCss}
    `;
      style.id = 'personaStyles';
      this.document.head.append(style);
    } else {
      setTimeout(() => {
        const style = getComputedStyle(document.body);
        for (let i = 1; i <= 9; i++) {
          this.colorAccent[`a${i}00`] = style.getPropertyValue(`--fuse-accent-${i}00`);
          this.colorAccent[`rgb${i}00`] = style.getPropertyValue(`--fuse-accent-${i}00-rgb`);
          this.colorPrimary[`a${i}00`] = style.getPropertyValue(`--fuse-primary-${i}00`);
          this.colorPrimary[`rgb${i}00`] = style.getPropertyValue(`--fuse-primary-${i}00-rgb`);
        }
      }, 1000);
    }
  }

  prepareColors(
    color: string,
    domainInput: number,
    prefix: string = 'a',
  ): { [key: string]: string } {
    GStatic.log('this.usePersonaState', this.usePersonaState);
    if (!this.usePersonaState) {
      return;
    }
    const Palette = this.generatePalette(color, 600);

    const newColors: any = {};
    const colors: any = {};

    Object.entries(Palette).forEach(([key, val]) => {
      newColors[`--fuse-${prefix}-${key}`] = val;
      newColors[`--fuse-${prefix}-${key}-rgb`] = this.hexToRgb(
        newColors[`--fuse-${prefix}-${key}`],
      );
      colors['a' + key] = newColors[`--fuse-${prefix}-${key}`];
      colors['rgb' + key] = newColors[`--fuse-${prefix}-${key}-rgb`];
    });
    newColors[`--fuse-${prefix}`] = color;
    newColors[`--fuse-${prefix}-rgb`] = this.hexToRgb(color);

    const compareColors = (mainColor, color2) => {
      let color_hex = '';
      let color_rgb = '';
      const contrast1 = chroma.contrast(mainColor, color2);
      const contrast2 = chroma.contrast(color, '#ffffff');
      const contrast3 = chroma.contrast(color, '#000000');
      if (contrast1 < 4.5 && contrast2 < 4.5 && contrast3 > 4.5) {
        color_hex = '#0000009f';
        color_rgb = '0,0,0';
      } else {
        color_hex = contrast1 > contrast2 ? newColors[`--fuse-${prefix}-900`] : '#FFFFFF';
        color_rgb = contrast1 > contrast2 ? newColors[`--fuse-${prefix}-900-rgb`] : '255,255,255';
      }
      return [color_hex, color_rgb];
    };

    Object.entries(Palette).forEach(([key, val]) => {
      const asd = chroma.contrast(val.toString(), newColors[`--fuse-${prefix}-900`]);
      [newColors[`--fuse-on-${prefix}-${key}`], newColors[`--fuse-on-${prefix}-${key}-rgb`]] =
        compareColors(val.toString(), newColors[`--fuse-${prefix}-900`]);
    });

    [newColors[`--fuse-on-${prefix}`], newColors[`--fuse-on-${prefix}-rgb`]] = compareColors(
      color,
      newColors[`--fuse-${prefix}-900`],
    );

    if (prefix === 'accent') {
      this.colorAccent = colors;
    } else if (prefix === 'primary') {
      this.colorPrimary = colors;
    }
    return newColors;
  }

  private rgbToHex(r, g, b): string {
    function componentToHex(c): string {
      const hex = c.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    }

    return '#' + componentToHex(r) + componentToHex(g) + componentToHex(b);
  }

  private hexToRgb(hex): any {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)].join()
      : null;
  }

  private generatePalette(config: any, domainInput: number = 600): any {
    // Prepare an empty palette

    const palette = {
      50: null,
      100: null,
      200: null,
      300: null,
      400: null,
      500: null,
      600: null,
      700: null,
      800: null,
      900: null,
    };

    // If a single color is provided,
    // assign it to the 500
    if (_.isString(config)) {
      palette[domainInput] = chroma.valid(config) ? config : null;
    }

    // If a partial palette is provided,
    // assign the values
    if (_.isPlainObject(config)) {
      if (!chroma.valid(config[500])) {
        throw new Error(
          'You must have a 500 hue in your palette configuration! Make sure the main color of your palette is marked as 500.',
        );
      }

      // Remove everything that is not a hue/color entry
      config = _.pick(config, Object.keys(palette));

      // Merge the values
      _.mergeWith(palette, config, (objValue, srcValue) =>
        chroma.valid(srcValue) ? srcValue : null,
      );
    }

    // Prepare the colors array
    const colors = Object.values(palette).filter((color) => color);

    // Generate a very dark and a very light versions of the
    // default color to use them as the boundary colors rather
    // than using pure white and pure black. This will stop
    // in between colors' hue values to slipping into the grays.
    colors.unshift(
      chroma.scale(['white', palette[domainInput]]).domain([0, 1]).mode('lrgb').colors(50)[1],
    );
    colors.push(
      chroma.scale(['black', palette[domainInput]]).domain([0, 1]).mode('lrgb').colors(10)[1],
    );

    // Prepare the domains array
    const domain = [
      0,
      ...Object.entries(palette)
        .filter(([key, value]) => value)
        .map(([key]) => parseInt(key) / 1000),
      1,
    ];

    // Generate the color scale
    const scale = chroma.scale(colors).domain(domain).mode('lrgb');

    // Build and return the final palette
    return {
      50: scale(0.05).hex(),
      100: scale(0.1).hex(),
      200: scale(0.2).hex(),
      300: scale(0.3).hex(),
      400: scale(0.4).hex(),
      500: scale(0.5).hex(),
      600: scale(0.6).hex(),
      700: scale(0.7).hex(),
      800: scale(0.8).hex(),
      900: scale(0.9).hex(),
    };
  }
}
